import React, { createContext, useState, useContext, useEffect } from 'react';
import { AuthContext } from './AuthContext';
import roleApi from '../api/roleApi';
import { resolveRole, updateRoleMap } from '../utils/roleUtils';

// Permission context
export const PermissionContext = createContext();

// Default permissions for each role - updated for the new permission system
const DEFAULT_ROLE_PERMISSIONS = {
  'admin': [
    'create_refund', 'update_refund', 'delete_refund', 'view_refunds', 'view_all_refunds',
    'update_refund_status', 'view_payment_methods', 'manage_users', 'manage_roles', 
    'manage_statuses', 'manage_payment_methods', 'manage_reasons', 'view_logs', 
    'view_reports', 'view_admin_panel', 'access_settings_module'
  ],
  'casi': [
    'create_refund', 'update_refund', 'view_refunds', 'view_all_refunds',
    'update_refund_status', 'view_payment_methods'
  ],
  // Diğer roller için varsayılan izinler - yeni bir rol oluşturulduğunda bunlar kullanılır
  // Bu, rol henüz izinleri veritabanında ayarlanmamışsa bir hata önlemi olarak kullanılır
  'default': ['view_refunds', 'view_payment_methods']
};

// Provider bileşeni
export const PermissionProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [rolePermissions, setRolePermissions] = useState(DEFAULT_ROLE_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  const [allDbRoles, setAllDbRoles] = useState([]);

  // Sayfa yüklendiğinde rol yetkilerini getir
  useEffect(() => {
    const fetchRolePermissions = async () => {
      if (user) {
        try {
          // Direkt olarak tüm rollerin izinlerini getiren API'yi kullan
          console.log('Temel rol izinleri getiriliyor...');
          const rolePermissionsResponse = await roleApi.getBasicRolePermissions();
          
          if (!rolePermissionsResponse || !rolePermissionsResponse.data) {
            console.error('Rol izin yanıtı beklenen formatta değil:', rolePermissionsResponse);
            throw new Error('Rol izinleri getirilemedi');
          }
          
          const rolesWithPermissions = rolePermissionsResponse.data;
          console.log(`${rolesWithPermissions.length} rol ve izinleri bulundu:`, rolesWithPermissions);
          
          // Rolleri önbellekle - roleUtils.js'e ekle
          updateRoleMap(rolesWithPermissions);
          setAllDbRoles(rolesWithPermissions);
          
          // İzinleri rol adlarına göre eşleştirecek nesne
          const permissionsMap = {};
          
          // Her rol için izinleri ekle
          rolesWithPermissions.forEach(role => {
            // Eğer rol için izinler varsa ve boş değilse kullan, yoksa varsayılan izinleri kullan
            const permissionsForRole = role.permissions && role.permissions.length > 0 
              ? role.permissions 
              : DEFAULT_ROLE_PERMISSIONS[role.name] || DEFAULT_ROLE_PERMISSIONS.default || [];
              
            permissionsMap[role.name] = permissionsForRole;
          });
          
          // Admin ve CASI için her zaman varsayılan izinleri ekle (güvenlik için)
          if (permissionsMap['admin']) {
            permissionsMap['admin'] = [...new Set([
              ...permissionsMap['admin'], 
              ...DEFAULT_ROLE_PERMISSIONS['admin']
            ])];
          } else {
            permissionsMap['admin'] = DEFAULT_ROLE_PERMISSIONS['admin'];
          }
          
          if (permissionsMap['casi']) {
            permissionsMap['casi'] = [...new Set([
              ...permissionsMap['casi'], 
              ...DEFAULT_ROLE_PERMISSIONS['casi']
            ])];
          } else {
            permissionsMap['casi'] = DEFAULT_ROLE_PERMISSIONS['casi'];
          }
          
          console.log("Yüklenen tüm rol izinleri:", permissionsMap);
          
          // Eğer herhangi bir rol izinleri geri dönmediyse varsayılanları kullan
          if (Object.keys(permissionsMap).length === 0) {
            setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
          } else {
            setRolePermissions(permissionsMap);
          }
        } catch (err) {
          console.error('Rol yetkileri alınamadı:', err);
          
          // Hata durumunda veya kullanıcı oturum açmamışsa, setup endpoint'lerini dene
          try {
            // Setup endpoint'lerini kullanarak rolleri ve izinleri getir
            const rolesResponse = await roleApi.getSetupRoles();
            const roles = rolesResponse.data;
            
            // Rolleri önbellekle
            updateRoleMap(roles);
            setAllDbRoles(roles);
            
            const permissionsMap = {};
            
            // Her rol için varsayılan izinleri ata
            for (const role of roles) {
              if (role.name === 'admin') {
                permissionsMap[role.name] = DEFAULT_ROLE_PERMISSIONS['admin'];
              } else if (role.name === 'casi') {
                permissionsMap[role.name] = DEFAULT_ROLE_PERMISSIONS['casi'];
              } else {
                permissionsMap[role.name] = DEFAULT_ROLE_PERMISSIONS.default || [];
              }
            }
            
            setRolePermissions(permissionsMap);
          } catch (setupErr) {
            console.error('Setup endpoint\'lerinden rol yetkileri alınamadı:', setupErr);
            // Son çare olarak varsayılan izinleri kullan
            setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
          }
        } finally {
          setLoading(false);
        }
      } else {
        // Kullanıcı oturum açmamış, setup endpoint'lerini dene
        try {
          // Setup endpoint'lerini kullanarak rolleri ve izinleri getir
          const rolesResponse = await roleApi.getSetupRoles();
          const roles = rolesResponse.data;
          
          // Rolleri önbellekle
          updateRoleMap(roles);
          setAllDbRoles(roles);
          
          const permissionsMap = {};
          
          // Her rol için varsayılan izinleri ata
          for (const role of roles) {
            if (role.name === 'admin') {
              permissionsMap[role.name] = DEFAULT_ROLE_PERMISSIONS['admin'];
            } else if (role.name === 'casi') {
              permissionsMap[role.name] = DEFAULT_ROLE_PERMISSIONS['casi'];
            } else {
              permissionsMap[role.name] = DEFAULT_ROLE_PERMISSIONS.default || [];
            }
          }
          
          setRolePermissions(permissionsMap);
        } catch (err) {
          console.error('Setup endpoint\'lerinden rol yetkileri alınamadı:', err);
          // Son çare olarak varsayılan izinleri kullan
          setRolePermissions(DEFAULT_ROLE_PERMISSIONS);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchRolePermissions();
  }, [user]);

  // Kullanıcının belirli bir yetkiye sahip olup olmadığını kontrol et
  const hasPermission = (permission) => {
    if (!user || !user.role_id) return false;
    
    // Önemli izinler için ek detay
    const isImportantPermission = ['create_refund', 'view_admin_panel'].includes(permission);
    
    if (isImportantPermission) {
      console.log(`DETAY - ${permission} izin kontrolü başlıyor - User ID: ${user.id}, Role ID: ${user.role_id}`);
    }
    
    // role_id kullanarak rol bilgisini al
    const role = resolveRole(user.role_id);
    if (role) {
      // Admin her zaman tüm izinlere sahiptir
      if (role.name === 'admin') {
        if (isImportantPermission) {
          console.log(`DETAY - ${permission} izni admin için otomatik onaylandı`);
        }
        return true;
      }
      
      // Debug bilgileri
      if (isImportantPermission) {
        console.log(`DETAY - Rol bilgisi:`, role);
        console.log(`DETAY - Rol izinleri:`, rolePermissions[role.name] || 'Bulunamadı');
      }
      
      // Rol bazlı izin kontrolü - Rolün sahip olduğu izinleri kontrol et
      // Eğer rol izinleri bulunamadıysa, varsayılan izinlere düş
      const roleHasPermissions = rolePermissions[role.name] && rolePermissions[role.name].length > 0;
      
      if (roleHasPermissions) {
        const hasPermission = rolePermissions[role.name].includes(permission);
        
        if (isImportantPermission) {
          console.log(`DETAY - ${permission} izni ${role.name} rolü için: ${hasPermission ? 'VAR' : 'YOK'}`);
          console.log(`DETAY - Rol izinleri:`, rolePermissions[role.name]);
        }
        
        return hasPermission;
      } else {
        // Admin ve CASI için varsayılan izinleri kullan
        if (role.name === 'admin') {
          const hasPermission = DEFAULT_ROLE_PERMISSIONS.admin.includes(permission);
          if (isImportantPermission) {
            console.log(`DETAY - ${permission} izni admin için varsayılan: ${hasPermission ? 'VAR' : 'YOK'}`);
          }
          return hasPermission;
        } else if (role.name === 'casi') {
          const hasPermission = DEFAULT_ROLE_PERMISSIONS.casi.includes(permission);
          if (isImportantPermission) {
            console.log(`DETAY - ${permission} izni casi için varsayılan: ${hasPermission ? 'VAR' : 'YOK'}`);
          }
          return hasPermission;
        } else {
          // Özel durumlar için kontrol - örneğin create_refund
          if (permission === 'create_refund' && role.name.includes('create_refund')) {
            if (isImportantPermission) {
              console.log(`DETAY - Rol adında 'create_refund' içerdiği için izin verildi: ${role.name}`);
            }
            return true;
          }
          
          // Diğer roller için varsayılan izinleri kullan
          const hasPermission = DEFAULT_ROLE_PERMISSIONS.default?.includes(permission) || false;
          if (isImportantPermission) {
            console.log(`DETAY - ${permission} izni ${role.name} rolü için varsayılan: ${hasPermission ? 'VAR' : 'YOK'}`);
          }
          
          return hasPermission;
        }
      }
    }
    
    // role_id var ama çözümlenemezse, basit ID kontrolü yap
    if (user.role_id === 1) {
      if (isImportantPermission) {
        console.log(`DETAY - ${permission} izni admin ID kontrolü için onaylandı`);
      }
      return true; // admin
    }
    
    if (isImportantPermission) {
      console.log(`DETAY - ${permission} izni reddedildi - rol bulunamadı`);
    }
    
    return false;
  };
  
  // Kullanıcının belirli bir role sahip olup olmadığını kontrol et
  const hasRole = (roleName) => {
    if (!user || !user.role_id) return false;
    
    // role_id kullanarak rol bilgisini al
    const role = resolveRole(user.role_id);
    
    // Admin her zaman tüm rollerin yetkilerine sahiptir
    if (role && role.name === 'admin') return true;
    
    // İstenen rolü kontrol et
    return role && role.name === roleName;
  };
  
  // Kullanıcının aktif rolü için tüm izinleri getir
  const getUserPermissions = () => {
    if (!user || !user.role_id) return [];
    
    // role_id kullanarak rol bilgisini al
    const role = resolveRole(user.role_id);
    if (role) {
      // Rol izinleri veri tabanından gelmiş mi kontrol et
      if (rolePermissions[role.name] && rolePermissions[role.name].length > 0) {
        // Özel durum kontrolü - veritabanında yok ama rolün adında belirtilmiş
        if (role.name.includes('create_refund') && !rolePermissions[role.name].includes('create_refund')) {
          console.log(`DETAY - create_refund izni, ${role.name} rolüne rol adından dolayı eklendi`);
          return [...rolePermissions[role.name], 'create_refund'];
        }
        return rolePermissions[role.name];
      }
      
      // Admin ve CASI için varsayılan izinleri kullan
      if (role.name === 'admin') {
        return DEFAULT_ROLE_PERMISSIONS.admin;
      } else if (role.name === 'casi') {
        return DEFAULT_ROLE_PERMISSIONS.casi;
      }
      
      // Özel durum kontrolü - veritabanında yok ama rolün adında belirtilmiş
      if (role.name.includes('create_refund')) {
        console.log(`DETAY - create_refund izni, ${role.name} rolüne rol adından dolayı eklendi`);
        return [...(DEFAULT_ROLE_PERMISSIONS.default || []), 'create_refund'];
      }
      
      // Diğer roller için varsayılan izinleri kullan
      return DEFAULT_ROLE_PERMISSIONS.default || [];
    }
    
    // role_id var ama çözümlenemezse boş dizi döndür
    return [];
  };
  
  // Tüm rol-izin ilişkilerini güncelle
  const updateRolePermissions = async (updatedPermissions) => {
    try {
      // Önce yerel state'i güncelle
      setRolePermissions(updatedPermissions);
      console.log('Yerel izinler (rolePermissions) güncellendi:', updatedPermissions);
      
      if (!user) {
        console.warn('Kullanıcı oturum açmadığı için izinler güncellenemiyor');
        return { success: false, message: 'Kullanıcı oturum açmadı' };
      }
      
      // Rolleri getirme stratejisi
      let roles = [];
      let rolesResponse;
      
      // Önce manage_roles izni olup olmadığını kontrol et
      const role = resolveRole(user.role_id);
      const isAdmin = role && role.name === 'admin';
      const hasManageRolesPermission = isAdmin || rolePermissions[role?.name]?.includes('manage_roles');
      
      // API'den rolleri getir - İzin durumuna göre doğru endpoint'i kullan
      console.log('Roller getiriliyor...');
      try {
        if (hasManageRolesPermission) {
          // Yönetici/yetkili kullanıcı - getAllRoles kullan
          console.log('manage_roles izni var, tüm roller getiriliyor');
          rolesResponse = await roleApi.getAllRoles();
        } else {
          // Normal kullanıcı - temel rol izinlerini getir
          console.log('manage_roles izni yok, temel rol izinleri getiriliyor');
          
          // Temel rol izinlerini getir ve gereken formata çevir
          const basicRolePermissionsResponse = await roleApi.getBasicRolePermissions();
          rolesResponse = {
            data: basicRolePermissionsResponse.data.map(rp => ({
              id: rp.id,
              name: rp.name,
              display_name: rp.display_name
            }))
          };
        }
        
        if (!rolesResponse || !rolesResponse.data) {
          console.error('Rol yanıtı beklenen formatta değil:', rolesResponse);
          throw new Error('Roller getirilemedi');
        }
        
        roles = rolesResponse.data;
        
        // Rolleri önbellekle
        updateRoleMap(roles);
        setAllDbRoles(roles);
        
        console.log(`${roles.length} rol bulundu:`, roles);
      } catch (roleError) {
        console.error('Rol bilgileri alınırken hata:', roleError);
        // Hata durumunda son çare olarak setup endpoint'i dene
        console.log('Setup endpoint ile rolleri almayı deniyorum...');
        rolesResponse = await roleApi.getSetupRoles();
        roles = rolesResponse.data || [];
        
        // Rolleri önbellekle
        updateRoleMap(roles);
        setAllDbRoles(roles);
        
        if (roles.length === 0) {
          console.error('Hiçbir rol getirilemedi');
          throw new Error('Roller getirilemedi');
        }
      }
      
      // İzinleri getirme stratejisi
      let allPermissions = [];
      
      try {
        if (hasManageRolesPermission) {
          // Yönetici/yetkili kullanıcı - getAllPermissions kullan
          console.log('Tüm izinler getiriliyor...');
          const allPermissionsResponse = await roleApi.getAllPermissions();
          
          if (!allPermissionsResponse || !allPermissionsResponse.data) {
            console.error('İzin yanıtı beklenen formatta değil:', allPermissionsResponse);
            throw new Error('İzinler getirilemedi');
          }
          
          allPermissions = allPermissionsResponse.data || [];
        } else {
          // Normal kullanıcı için - temel rol izinlerinden izinleri al
          console.log('Temel rol izinlerinden izin listesi oluşturuluyor...');
          const basicRolePermissionsResponse = await roleApi.getBasicRolePermissions();
          
          // Tüm izinleri birleştir ve tekrar edenleri kaldır
          const uniquePermissions = new Set();
          basicRolePermissionsResponse.data.forEach(rp => {
            rp.permissions.forEach(p => uniquePermissions.add(p));
          });
          
          // Setup permissions'dan ID'leri elde et
          const setupPermissionsResponse = await roleApi.getSetupPermissions();
          
          // İzin adlarını ve ID'lerini eşleştir
          allPermissions = [...uniquePermissions].map(permName => {
            const matchingPerm = setupPermissionsResponse.data.find(p => p.name === permName);
            return matchingPerm || { 
              id: -1, // Geçici ID
              name: permName,
              description: 'Unknown permission'
            };
          });
        }
        
        console.log(`${allPermissions.length} izin bulundu:`, allPermissions);
      } catch (permError) {
        console.error('İzin bilgileri alınırken hata:', permError);
        // Hata durumunda setup izinlerini dene
        console.log('Setup permissions ile izinleri almayı deniyorum...');
        try {
          const setupPermissionsResponse = await roleApi.getSetupPermissions();
          allPermissions = setupPermissionsResponse.data || [];
          
          if (allPermissions.length === 0) {
            console.error('Setup endpoint\'inden de izin getirilemedi');
            return { success: false, message: 'İzinler alınamadı' };
          }
        } catch (setupError) {
          console.error('Setup izinleri alınırken hata:', setupError);
          return { success: false, message: 'İzinler alınamadı' };
        }
      }
      
      let successCount = 0;
      let errorCount = 0;
      let errorMessages = [];
      
      // Her rol için izinleri güncelle
      for (const role of roles) {
        console.log(`Rol işleniyor: ${role.name} (ID: ${role.id})`);
        
        if (updatedPermissions[role.name]) {
          // İzin adlarını ID'lere dönüştür
          const permissionIds = [];
          const notFoundPermissions = [];
          
          console.log(`${role.name} rolü için güncellenecek izinler:`, updatedPermissions[role.name]);
          
          for (const permName of updatedPermissions[role.name]) {
            const perm = allPermissions.find(p => p.name === permName);
            if (perm) {
              permissionIds.push(perm.id);
            } else {
              notFoundPermissions.push(permName);
              console.warn(`'${permName}' izni bulunamadı`);
            }
          }
          
          if (notFoundPermissions.length > 0) {
            console.warn(`'${role.name}' rolü için bulunamayan izinler:`, notFoundPermissions);
          }
          
          console.log(`'${role.name}' rolü için dönüştürülen izin ID'leri (${permissionIds.length}):`, permissionIds);
          
          // Rol izinlerini güncelle
          try {
            console.log(`'${role.name}' rolü (ID: ${role.id}) için izinler güncelleniyor...`);
            const updateResponse = await roleApi.updateRolePermissions(role.id, permissionIds);
            console.log(`'${role.name}' rolü izinleri güncellendi:`, updateResponse);
            
            // Sunucudan güncel izinleri tekrar al ve kontrol et
            try {
              const updatedPermsResponse = await roleApi.getRolePermissions(role.id);
              const updatedPerms = updatedPermsResponse.data || [];
              console.log(`Sunucudan alınan güncel izinler (${role.name}):`, updatedPerms);
              
              // Doğrulama: İzinlerin gerçekten eklenip eklenmediğini kontrol et
              const actualPermissionNames = updatedPerms.map(p => p.name);
              const expectedPermissionNames = updatedPermissions[role.name];
              
              // Eksik izinleri bul
              const missingPermissions = expectedPermissionNames.filter(
                p => !actualPermissionNames.includes(p)
              );
              
              if (missingPermissions.length > 0) {
                console.warn(`'${role.name}' rolü için bazı izinler eklenemedi:`, missingPermissions);
                // Uyarı ekle ama başarısız kabul etme
              }
            } catch (verifyError) {
              console.error(`'${role.name}' rolü için izinleri doğrulama hatası:`, verifyError);
            }
            
            successCount++;
          } catch (updateError) {
            errorCount++;
            const errorMsg = `'${role.name}' rolü için izin güncellenemedi: ${updateError.message || 'Bilinmeyen hata'}`;
            errorMessages.push(errorMsg);
            console.error(errorMsg, updateError);
          }
        } else {
          console.log(`${role.name} rolü için güncellenecek izin bulunamadı, atlanıyor`);
        }
      }
      
      console.log(`İzin güncelleme tamamlandı: ${successCount} başarılı, ${errorCount} başarısız`);
      
      return { 
        success: successCount > 0, 
        message: errorCount > 0 
          ? `Güncelleme yapılırken ${errorCount} hata oluştu: ${errorMessages.join('; ')}` 
          : 'Tüm izinler başarıyla güncellendi'
      };
    } catch (error) {
      console.error('İzinleri güncellerken kritik hata:', error);
      return { 
        success: false, 
        message: `İzinler güncellenirken hata oluştu: ${error.message || 'Bilinmeyen hata'}`
      };
    }
  };

  return (
    <PermissionContext.Provider value={{ 
      hasPermission, 
      rolePermissions, 
      updateRolePermissions,
      getUserPermissions, 
      loading,
      hasRole,
      allDbRoles
    }}>
      {children}
    </PermissionContext.Provider>
  );
};

// Custom hook for using the permission context
export const usePermission = () => useContext(PermissionContext); 